import { Link } from "react-router-dom";
import { ArrowUpRight, Building2 } from "lucide-react";
import { SectionHead, Reveal, Crosshair } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";

export const GroupSection = () => {
  const { lang, t } = useLang();
  const g = t.group;
  const q = lang === "en" ? "" : `?lang=${lang}`;
  return (
    <section id="group" className="relative overflow-hidden bg-sand border-b border-technical py-24 sm:py-32" data-testid="group-section">
      <Crosshair className="top-6 left-6" />
      <Crosshair className="bottom-6 right-6" />
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={g.eyebrow} title={g.title} desc={g.desc} testid="group" />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow" data-testid="group-companies-label">{g.companiesLabel}</p>
            </Reveal>
            <ol className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[rgba(22,24,27,0.12)] border border-technical" data-testid="group-companies">
              {g.companies.map((c, i) => {
                const cls = `group flex h-full flex-col justify-between gap-4 p-5 transition-colors duration-200 hover:bg-canvas ${c.holding ? "bg-ink text-[#f8f7f4] hover:bg-ink" : "bg-white"}`;
                const inner = (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <span className={`font-mono-tech text-[10px] font-semibold ${c.holding ? "text-[#e63946]" : "text-red-brand"}`}>{String(i + 1).padStart(2, "0")}</span>
                      <ArrowUpRight className={`h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${c.holding ? "text-[#f8f7f4]/60" : "text-[#525964]"}`} />
                    </div>
                    <div>
                      <p className={`font-display text-base sm:text-lg font-bold tracking-tight ${c.holding ? "text-[#f8f7f4]" : "text-ink group-hover:text-red-brand"}`}>
                        {c.name}
                        {c.holding && <span className="ml-2 align-middle border border-[#e63946]/60 px-1.5 py-0.5 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-[#e63946]">{g.holding}</span>}
                      </p>
                      <p className={`mt-1.5 text-xs leading-relaxed ${c.holding ? "text-[#f8f7f4]/60" : "text-[#525964]"}`}>{c.role}</p>
                    </div>
                  </>
                );
                const tid = `group-company-${c.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`;
                return (
                  <li key={c.name} className="contents">
                    {c.internal ? (
                      <Link to={`${c.url}${q}`} data-testid={tid} className={cls}>{inner}</Link>
                    ) : (
                      <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={tid} className={cls}>{inner}</a>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="chamfer border border-technical bg-ink bp-grid-dark p-7 sm:p-9" data-testid="group-contact-card">
                <p className="flex items-center gap-2 font-mono-tech text-[0.65rem] tracking-[0.28em] uppercase text-[#e63946]">
                  <Building2 className="h-4 w-4" /> {g.contactEyebrow}
                </p>
                <p className="mt-4 font-display text-xl sm:text-2xl font-bold leading-snug tracking-tight text-[#f8f7f4]">{g.contactTitle}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#f8f7f4]/60">{g.contactDesc}</p>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  <a href="https://arthurtoren.co.uk" target="_blank" rel="noopener noreferrer" data-testid="group-cta-coke" className="chamfer-sm inline-flex items-center gap-2 bg-red-brand px-5 py-3 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] hover:bg-[#5f1019] transition-colors">
                    {g.cta} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
                <p className="mt-6 font-mono-tech text-[10px] tracking-[0.15em] text-[#f8f7f4]/45">
                  <a href="mailto:office@margtor.africa" data-testid="group-email" className="hover:text-[#f8f7f4]">office@margtor.africa</a> · <a href="https://wa.me/254708189175" target="_blank" rel="noopener noreferrer" data-testid="group-whatsapp" className="hover:text-[#f8f7f4]">WhatsApp +254 708 189 175</a>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
        {g.supporters && (
          <div className="mt-20" data-testid="group-supporters">
            <Reveal>
              <p className="eyebrow">{g.supportersEyebrow}</p>
              <h3 className="mt-4 font-display text-2xl sm:text-3xl font-black tracking-tight text-ink">{g.supportersTitle}</h3>
              <p className="mt-3 max-w-2xl text-sm sm:text-base leading-relaxed text-[#525964]">{g.supportersDesc}</p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {g.supporters.map((s, i) => (
                <Reveal key={s.url} delay={i * 0.08}>
                  <article data-testid={`supporter-${i}`} className="group flex h-full flex-col border border-technical bg-white p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(122,22,32,0.45)] hover:shadow-[0_18px_40px_-20px_rgba(22,24,27,0.35)]">
                    <span className="font-mono-tech text-[9.5px] uppercase tracking-[0.18em] text-red-brand">{s.tag}</span>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`supporter-${i}-link`} className="mt-3 inline-flex items-center gap-2 font-display text-xl sm:text-2xl font-bold tracking-tight text-ink group-hover:text-red-brand transition-colors">
                      {s.name} <ArrowUpRight className="h-4 w-4" />
                    </a>
                    <p className="mt-4 text-sm leading-relaxed text-[#525964]">{s.mission}</p>
                    <p className="mt-4 border-l-2 border-red-brand pl-4 text-sm leading-relaxed text-ink">{s.value}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
